import React, { Component } from "react";
import { PublicClientApplication, EventType, InteractionRequiredAuthError } from "@azure/msal-browser";
import getadb2cauth from "./getadb2cauth";
import registerServiceWorker from "./registerServiceWorker";
import { createFakeAuth } from "./mocks/fakeAuth";
import {
    addMobileAuthMessageListener,
    createMobileAccountFromToken,
    createMobileMsalInstance,
    getTokenFromMobileAuthMessage,
    installMobileAuthFetchInterceptor,
    isMobileAuthMode,
    postMobileAuthMessage,
    requestMobileAuthToken
} from "./mobileAuthBridge";

export const AuthContext = React.createContext();

class AuthProvider extends Component {
    mobileAuthCleanup = null;
    mobileInvalidTokenRefreshAttempted = false;

    state = {
        msalInstance: null,
        isLoggedIn: false,
        isAuthenticating: true,
        idToken: "",
        scopes: [],
        redirectUri: "",
        authError: null,
        isMobileAuth: false,
    };

    componentDidMount() {
        this.initializeAuth();
    }

    componentWillUnmount() {
        if (this.mobileAuthCleanup) this.mobileAuthCleanup();
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevState.isLoggedIn && this.state.isLoggedIn && !this.state.isMobileAuth) {
            registerServiceWorker();
        }
    }

    // Função auxiliar para limpar cookies específicos do B2C
    clearB2CCookies = () => {
        const cookiesToClear = [
            'x-ms-cpim-',
            'x-ms-cpim-csrf',
            'x-ms-cpim-trans',
            'x-ms-cpim-dc',
        ];

        document.cookie.split(";").forEach((c) => {
            const cookieName = c.split("=")[0].trim();
            
            // Verificar se o nome do cookie começa com qualquer uma das strings que queremos limpar
            const shouldClear = cookiesToClear.some(clearName => cookieName.includes(clearName));
            
            if (shouldClear) {
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
            }
        });

        console.log("Cookies B2C limpos");
    };

    // Função auxiliar para limpar todo o storage e cache
    clearAllStorageAndCache = () => {
        try {
            // Limpar cookies B2C espec�ficos
            this.clearB2CCookies();

            // Limpar sessionStorage
            sessionStorage.clear();

            // Limpar localStorage
            localStorage.clear();

            // Limpar cache espec�fico do MSAL
            const mSalKeys = [
                'msal.token.keys',
                'msal.account.keys',
                'msal.idtoken',
                'msal.accesstoken',
                'msal.refreshtoken',
            ];

            mSalKeys.forEach(key => {
                localStorage.removeItem(key);
                sessionStorage.removeItem(key);
            });

            // Limpar todos os itens com prefixo msal
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('msal.')) {
                    localStorage.removeItem(key);
                }
            });

            Object.keys(sessionStorage).forEach(key => {
                if (key.startsWith('msal.')) {
                    sessionStorage.removeItem(key);
                }
            });

            console.log("Todo o storage e cache foram limpos");
        } catch (error) {
            console.error("Erro ao limpar storage:", error);
        }
    };

    initializeAuth = async () => {
        try {
            if (isMobileAuthMode()) {
                await this.initializeMobileAuth();
                return;
            }

            const isFakeAuth = process.env.REACT_APP_FAKE_AUTH === "true" && !isMobileAuthMode();
            
            if (isFakeAuth) {
                setTimeout(() => {
                    console.log("Auth DEV mode");    
                }, 500);                

                const fake = createFakeAuth();

                this.setState({
                    msalInstance: fake.msalInstance,
                    scopes: fake.scopes,
                    redirectUri: fake.redirectUri,
                    isLoggedIn: true,
                    isAuthenticating: false,
                    idToken: fake.idToken,
                    authError: null
                });

                return;
            }

            const config = await getadb2cauth();

            const msalConfig = {
                auth: {
                    clientId: config.applicationId,
                    authority: `${config.instance}/${config.tenant}/${config.signInPolicy}`,
                    knownAuthorities: [`${config.instance}`],
                    redirectUri: config.redirectUri,
                    postLogoutRedirectUri: config.redirectUri,
                    navigateToLoginRequestUrl: true,
                },
                cache: {
                    cacheLocation: "sessionStorage",
                    storeAuthStateInCookie: true,
                    secureCookies: false,
                }
            };

            const msalInstance = new PublicClientApplication(msalConfig);

            await msalInstance.initialize();

            this.setState({
                msalInstance,
                scopes: config.scopes,
                redirectUri: config.redirectUri
            });

            this.setupEventCallbacks(msalInstance);

            await this.processAuthentication(msalInstance, config);

        } catch (error) {
            console.log("ERRO COMPLETO:", error);
            console.log("STACK:", error?.stack);

            this.setState({
                isAuthenticating: false,
                authError: error.message
            });
        }
    };

    initializeMobileAuth = async () => {
        try {
            const msalInstance = createMobileMsalInstance();
            const removeMessageListener = addMobileAuthMessageListener((message) => {
                const token = getTokenFromMobileAuthMessage(message);

                if (token) this.handleMobileAuthToken(token, msalInstance);
            });
            const restoreFetch = installMobileAuthFetchInterceptor((token) => {
                this.handleMobileAuthToken(token, msalInstance);
            });

            this.mobileAuthCleanup = () => {
                removeMessageListener();
                restoreFetch();
            };

            this.setState({
                msalInstance,
                scopes: [],
                redirectUri: window.location.href,
                isMobileAuth: true,
                authError: null,
            });

            postMobileAuthMessage("MOBILE_AUTH_READY");

            const idToken = await requestMobileAuthToken();
            await this.handleMobileAuthToken(idToken, msalInstance);
        } catch (error) {
            console.error("Erro ao inicializar autenticacao mobile:", error);

            this.setState({
                isAuthenticating: false,
                authError: error.message
            });
        }
    };

    handleMobileAuthToken = async (idToken, msalInstance = this.state.msalInstance, hasRetried = false) => {
        if (!idToken) return;

        if (this.state.isLoggedIn && this.state.idToken === idToken) return;

        if (!this.isTokenValid(idToken)) {
            if (!hasRetried && !this.mobileInvalidTokenRefreshAttempted) {
                this.mobileInvalidTokenRefreshAttempted = true;
                const refreshedToken = await requestMobileAuthToken({ forceRefresh: true });
                await this.handleMobileAuthToken(refreshedToken, msalInstance, true);
                return;
            }

            postMobileAuthMessage("AUTH_TOKEN_INVALID");

            this.setState({
                isAuthenticating: false,
                authError: "Token mobile invalido ou expirado"
            });
            return;
        }

        this.mobileInvalidTokenRefreshAttempted = false;
        const account = createMobileAccountFromToken(idToken);

        msalInstance?.setActiveAccount(account);

        this.setState({
            msalInstance,
            isLoggedIn: true,
            isAuthenticating: false,
            idToken,
            authError: null,
            isMobileAuth: true,
        });
    };

    refreshMobileToken = async () => {
        const idToken = await requestMobileAuthToken({ forceRefresh: true });
        await this.handleMobileAuthToken(idToken);

        return idToken;
    };

    setupEventCallbacks = (msalInstance) => {
        msalInstance.addEventCallback((event) => {
            // console.log("MSAL Event:", event.eventType, event);

            switch (event.eventType) {
                case EventType.LOGIN_SUCCESS:
                case EventType.ACQUIRE_TOKEN_SUCCESS:
                case EventType.SSO_SILENT_SUCCESS:
                    const account = event.payload?.account;
                    if (account) {
                        msalInstance.setActiveAccount(account);
                    }
                    break;

                case EventType.LOGIN_FAILURE:
                case EventType.ACQUIRE_TOKEN_FAILURE:
                case EventType.SSO_SILENT_FAILURE:
                    console.error("Autentica��o falhou:", event.error);
                    this.handleAuthError(event.error);
                    break;

                case EventType.LOGOUT_SUCCESS:
                    this.clearAuthState();
                    break;
            }
        });
    };

    processAuthentication = async (msalInstance, config) => {
        try {
            const redirectResponse = await msalInstance.handleRedirectPromise();

            if (redirectResponse) {
                // console.log("Resposta de redirect recebida:", redirectResponse);
                await this.handleAuthResponse(redirectResponse, msalInstance);
            } else {
                await this.checkExistingSession(msalInstance);
            }
        } catch (error) {
            console.error("Erro ao processar autentica��o:", error);

            if (error.message?.includes("AADB2C90118")) {
                this.redirectToPasswordReset(config);
            } else if (error.message?.includes("AADB2C90151") || error.message?.includes("AADB2C90157")) {
                await this.clearCacheAndRedirect(error.message);
            } else {
                await this.clearCacheAndRedirect(error.message)
            }
        }
    };

    checkExistingSession = async (msalInstance) => {
        try {
            const accounts = msalInstance.getAllAccounts();
            console.log("Contas encontradas:", accounts.length);

            if (accounts.length === 0) {
                console.log("Nenhuma conta encontrada, iniciando login...");
                await this.performLogin(msalInstance);
                return;
            }

            const account = msalInstance.getActiveAccount() || accounts[0];
            msalInstance.setActiveAccount(account);

            const tokenRequest = {
                account: account,
                scopes: this.state.scopes,
                forceRefresh: false,
            };

            const response = await msalInstance.acquireTokenSilent(tokenRequest);

            if (this.isTokenValid(response.idToken)) {
                await this.handleAuthResponse(response, msalInstance);
            } else {
                console.log("Token inv�lido, renovando...");
                tokenRequest.forceRefresh = true;
                const refreshedResponse = await msalInstance.acquireTokenSilent(tokenRequest);
                await this.handleAuthResponse(refreshedResponse, msalInstance);
            }

        } catch (error) {
            // console.error("Erro ao verificar sess�o existente:", error);

            if (error instanceof InteractionRequiredAuthError) {
                console.log("Intera��o necess�ria, redirecionando para login...");
                await this.performLogin(msalInstance);
            } else {
                await this.clearCacheAndLogin(msalInstance);
            }
        }
    };

    handleAuthResponse = async (response, msalInstance) => {
        try {
            if (!response || !response.idToken) {
            }

            const account = response.account;
            const idToken = response.idToken;

            if (!this.isTokenValid(idToken)) {
                console.info("Token inv�lido ou expirado, solicitando novo...");
                await this.clearCacheAndLogin(msalInstance);
                return;
            }

            msalInstance.setActiveAccount(account);
            
            this.setState({
                isLoggedIn: true,
                isAuthenticating: false,
                idToken,                
                authError: null,
            });

            // console.log("Autentica��o bem-sucedida!");

        } catch (error) {
            console.error("Erro ao processar resposta de autentica��o:", error);
            await this.clearCacheAndLogin(msalInstance);
        }
    };

    isTokenValid = (token) => {
        if (!token) {
            // console.log("Token vazio");
            return false;
        }

        try {
            const parts = token.split('.');
            if (parts.length !== 3) {
                // console.error("Formato de token inv�lido");
                return false;
            }

            const base64Url = parts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
            const payload = JSON.parse(atob(padded));

            const now = Date.now() / 1000;
            const exp = payload.exp;
            const bufferTime = 30;

            if (now >= exp - bufferTime) {
                console.log(`Token expirando em ${exp - now} segundos`);
                return false;
            }

            const requiredClaims = this.state.isMobileAuth
                ? ['sub', 'iat', 'exp']
                : ['sub', 'iat', 'exp', 'extension_clientId'];
            for (const claim of requiredClaims) {
                if (!payload[claim]) {
                    console.error(`Claim essencial ausente: ${claim}`);
                    return false;
                }
            }

            return true;

        } catch (error) {
            console.error("Erro ao validar token:", error);
            return false;
        }
    };

    clearCacheAndLogin = async (msalInstance) => {
        if (this.state.isMobileAuth) {
            await this.refreshMobileToken();
            return;
        }

        console.log("Limpando cache e redirecionando para login...");

        try {
            const accounts = msalInstance.getAllAccounts();
            for (const account of accounts) {
                await msalInstance.removeAccount(account);
            }

            // Usar a função melhorada para limpar storage
            this.clearAllStorageAndCache();

            await new Promise(resolve => setTimeout(resolve, 100));

            await this.performLogin(msalInstance);

        } catch (error) {
            // console.error("Erro ao limpar cache:", error);
        }
    };

    clearCacheAndRedirect = async (errorMessage) => {
        // Usar a função melhorada para limpar storage
        this.clearAllStorageAndCache();

        let errorPage = "/Response/Error";
        if (errorMessage.includes("AADB2C90151")) {
            errorPage = "/Response/AADB2C90151";
        } else if (errorMessage.includes("AADB2C90157")) {
            errorPage = "/Response/AADB2C90157";
        }

        window.location.replace(errorPage);
    };

    performLogin = async (msalInstance) => {
        const { scopes, redirectUri } = this.state;

        if (this.state.isMobileAuth) {
            await this.refreshMobileToken();
            return;
        }

        if (!msalInstance) {
            // console.error("MSAL instance n�o inicializada");
            return;
        }

        try {
            const loginRequest = {
                scopes: scopes || [],
                redirectUri: redirectUri,
                prompt: "select_account",
            };

            // console.log("Iniciando login redirect com request:", loginRequest);
            await msalInstance.loginRedirect(loginRequest);

        } catch (error) {
            console.error("Erro ao realizar login:", error);
            this.setState({
                isAuthenticating: false,
                authError: error.message
            });
        }
    };

    redirectToPasswordReset = (config) => {
        // console.log("Redirecionando para reset de senha...");
        const { msalInstance } = this.state;

        if (msalInstance) {
            msalInstance.loginRedirect({
                authority: `${config.instance}/${config.tenant}/B2C_1A_PasswordReset`,
                redirectUri: config.redirectUri,
            });
        }
    };

    handleAuthError = (error) => {
        // console.error("Erro de autentica��o:", error);
        this.setState({
            authError: error?.message || "Erro desconhecido",
            isAuthenticating: false
        });
    };

    clearAuthState = () => {
        this.setState({
            isLoggedIn: false,
            idToken: "",
            authError: null,
        });
    };

    logout = async () => {
        const { msalInstance, isMobileAuth } = this.state;

        if (isMobileAuth) {
            postMobileAuthMessage("AUTH_LOGOUT");
            this.clearAuthState();
            return;
        }

        if (msalInstance) {
            try {
                const account = msalInstance.getActiveAccount();
                if (account) {
                    await msalInstance.logoutRedirect({
                        account: account,
                        postLogoutRedirectUri: this.state.redirectUri,
                    });
                }
            } catch (error) {
                // console.error("Erro ao fazer logout:", error);
                // Limpar todo o storage em caso de erro no logout
                this.clearAllStorageAndCache();
                window.location.reload();
            }
        }
    };

    render() {
        const { children } = this.props;
        const { msalInstance, isLoggedIn, idToken, isAuthenticating, authError, isMobileAuth } = this.state;

        if (!msalInstance || isAuthenticating) {
            return <div></div>;
        }

        return (
            <AuthContext.Provider
                value={{
                    msalInstance,
                    isLoggedIn,
                    idToken,
                    authError,
                    isMobileAuth,
                    refreshToken: this.refreshMobileToken,
                    performLoginRedirect: () => this.performLogin(msalInstance),
                    logout: this.logout,
                }}>
                {children}
            </AuthContext.Provider>
        );
    }


    parseJwt = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );

            return JSON.parse(jsonPayload);
        } catch (e) {
            console.error("Erro ao decodificar token", e);
            return null;
        }
    };
}

export default AuthProvider;
